import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Select, MenuItem, TextField, InputAdornment, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Checkbox, Avatar, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  CircularProgress, Skeleton, TablePagination, ButtonBase, LinearProgress, Grid,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import * as XLSX from 'xlsx';
import { costsApi } from '../services/api';
import EmptyState from '../components/common/EmptyState';
import { ErrorState } from '../components/common/States';
import SubTabs from '../components/common/SubTabs';
import ComingSoon from '../components/common/ComingSoon';
import TaxesPanel from '../components/TaxesPanel';
import { useShell } from '../components/Layout';
import { can } from '../utils/access';
import { formatMoney, formatNumber, formatDate, mskToday, apiError } from '../utils/format';
import { hasSync, idPrefix } from '../utils/marketplaces';
import { tokens } from '../theme';

const FROM_START = '2000-01-01';
const MONEY_RE = /^\d{1,10}([.,]\d{1,2})?$/;

// Two ways to change a cost: fix a mistake (the new value replaces the wrong one for every sale it
// covered) or record a real price change from a date (older sales keep the old price).
const ModeCard = ({ active, icon, title, text, onClick }) => (
  <ButtonBase onClick={onClick} aria-pressed={active}
    sx={{ flex: 1, display: 'block', textAlign: 'left', p: 1.75, borderRadius: '12px', border: `2px solid ${active ? tokens.primary : tokens.border}`,
      bgcolor: active ? tokens.primaryTint : tokens.paper, transition: 'border-color .15s, background-color .15s' }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, color: active ? tokens.primaryDark : tokens.ink }}>
      {icon}
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    </Stack>
    <Typography variant="body2" color="text.secondary">{text}</Typography>
  </ButtonBase>
);

const SetCostDialog = ({ open, onClose, accountId, products, mp }) => {
  const [value, setValue] = useState('');
  const [mode, setMode] = useState('fix');
  const [date, setDate] = useState(mskToday());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const anyCost = products.some((p) => p.cost_price != null);

  useEffect(() => {
    if (!open) return;
    setError('');
    setValue(products.length === 1 && products[0].cost_price != null ? String(products[0].cost_price) : '');
    setMode('fix');
    setDate(mskToday());
  }, [open, products]);

  const valid = MONEY_RE.test(value.trim()) && (mode === 'fix' || (date && date <= mskToday()));
  const save = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError('');
    const cost = value.trim().replace(',', '.');
    try {
      if (mode === 'date') {
        await costsApi.set(accountId, date, products.map((p) => ({ nm_id: p.nm_id, cost_price: cost })));
      } else {
        // "fix": overwrite the value in force for each product (or set it for all sales if there is none)
        const groups = {};
        products.forEach((p) => {
          const from = p.valid_from || FROM_START;
          (groups[from] = groups[from] || []).push({ nm_id: p.nm_id, cost_price: cost });
        });
        for (const [from, items] of Object.entries(groups)) {
          // eslint-disable-next-line no-await-in-loop
          await costsApi.set(accountId, from, items);
        }
      }
      onClose(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="sm" fullWidth PaperProps={{ component: 'form', onSubmit: save }}>
      <DialogTitle>{products.length === 1 ? 'Себестоимость товара' : `Себестоимость: ${products.length} товаров`}</DialogTitle>
      <DialogContent>
        {products.length === 1 && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
            <Avatar variant="rounded" src={products[0].photo_url || undefined} alt="" sx={{ width: 40, height: 52, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
              <ImageOutlinedIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }} noWrap>{products[0].title || 'Без названия'}</Typography>
              <Typography variant="body2" color="text.secondary">{products[0].vendor_code || '—'} · {idPrefix(mp)} {products[0].nm_id}</Typography>
            </Box>
          </Stack>
        )}
        <TextField autoFocus fullWidth label="Себестоимость за единицу" value={value} onChange={(e) => setValue(e.target.value)}
          error={Boolean(value) && !MONEY_RE.test(value.trim())}
          helperText={value && !MONEY_RE.test(value.trim()) ? 'Число, не больше двух знаков после запятой' : 'Закупка, упаковка, доставка до склада — всё, во что обходится единица товара'}
          InputProps={{ endAdornment: <InputAdornment position="end">₽</InputAdornment> }} inputProps={{ inputMode: 'decimal' }} sx={{ mb: 2.5 }} />
        {anyCost ? (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <ModeCard active={mode === 'fix'} onClick={() => setMode('fix')} icon={<BuildCircleOutlinedIcon fontSize="small" />} title="Исправить ошибку"
                text="Новое значение заменит неверное — прибыль за прошлые периоды пересчитается." />
              <ModeCard active={mode === 'date'} onClick={() => setMode('date')} icon={<EventRepeatOutlinedIcon fontSize="small" />} title="Цена изменилась"
                text="Старые продажи останутся по старой цене, новые — по новой." />
            </Stack>
            {mode === 'date' && (
              <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} inputProps={{ max: mskToday() }}
                InputLabelProps={{ shrink: true }} label="Новая цена действует с" sx={{ mt: 2 }} />
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">Значение применится ко всем продажам этого товара.</Typography>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>Отмена</Button>
        <Button type="submit" variant="contained" disabled={!valid || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
};

const AddArticleDialog = ({ open, onClose, accountId, mp }) => {
  const [nm, setNm] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setNm(''); setValue(''); setError(''); } }, [open]);
  const nmOk = /^\d{1,12}$/.test(nm.trim()) && Number(nm) > 0;
  const valid = nmOk && MONEY_RE.test(value.trim());
  const save = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      await costsApi.set(accountId, FROM_START, [{ nm_id: Number(nm), cost_price: value.trim().replace(',', '.') }]);
      onClose(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="xs" fullWidth PaperProps={{ component: 'form', onSubmit: save }}>
      <DialogTitle>Добавить товар</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Товары из кабинета появляются здесь сами. Добавьте вручную, только если товара ещё нет в списке.
        </Typography>
        <TextField fullWidth autoFocus label={mp === 'ozon' ? 'SKU Ozon' : 'Артикул WB'} value={nm} onChange={(e) => setNm(e.target.value)} error={Boolean(nm) && !nmOk}
          helperText={nm && !nmOk ? 'Только цифры' : ' '} inputProps={{ inputMode: 'numeric' }} sx={{ mb: 1 }} />
        <TextField fullWidth label="Себестоимость за единицу" value={value} onChange={(e) => setValue(e.target.value)}
          error={Boolean(value) && !MONEY_RE.test(value.trim())} InputProps={{ endAdornment: <InputAdornment position="end">₽</InputAdornment> }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>Отмена</Button>
        <Button type="submit" variant="contained" disabled={!valid || saving}>Добавить</Button>
      </DialogActions>
    </Dialog>
  );
};

const ImportDialog = ({ open, onClose, accountId, mp }) => {
  const [rows, setRows] = useState(null);
  const [problems, setProblems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (open) { setRows(null); setProblems([]); setError(''); } }, [open]);

  const parse = async (file) => {
    setError('');
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, blankrows: false, raw: false });
      const header = (sheet[0] || []).map((h) => String(h || '').trim().toLowerCase());
      const nmCol = header.findIndex((h) => h.includes('артикул wb') || h === 'nm_id' || h === 'артикул' || h === 'sku' || h.includes('sku ozon'));
      const costCol = header.findIndex((h) => h.includes('себестоимость') || h === 'cost_price');
      if (nmCol < 0 || costCol < 0) {
        setError(`В первой строке файла нужны колонки «${mp === 'ozon' ? 'SKU Ozon' : 'Артикул WB'}» и «Себестоимость».`);
        return;
      }
      const ok = [];
      const bad = [];
      sheet.slice(1).forEach((r, i) => {
        const nm = String(r[nmCol] ?? '').trim();
        const cost = String(r[costCol] ?? '').trim().replace(/\s/g, '').replace(',', '.');
        if (!nm && !cost) return;
        if (!cost) return; // empty cost in the template = leave as is
        if (!/^\d{1,12}$/.test(nm) || !/^\d{1,10}(\.\d{1,2})?$/.test(cost)) bad.push(i + 2);
        else ok.push({ nm_id: Number(nm), cost_price: cost });
      });
      setRows(ok);
      setProblems(bad);
    } catch (e) {
      setError('Не удалось прочитать файл. Поддерживаются XLSX и CSV.');
    }
  };

  const save = async () => {
    if (!rows?.length || saving) return;
    setSaving(true);
    setError('');
    try {
      await costsApi.set(accountId, FROM_START, rows);
      onClose(true);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Загрузить себестоимость из файла</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Скачайте шаблон со всеми товарами, заполните колонку «Себестоимость» и загрузите файл обратно. Пустые строки не меняются.
        </Typography>
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => e.target.files?.[0] && parse(e.target.files[0])} />
        <Button variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={() => inputRef.current?.click()}>Выбрать файл</Button>
        {rows && (
          <Alert severity={problems.length ? 'warning' : 'success'} sx={{ mt: 2 }}>
            Готово к загрузке: {formatNumber(rows.length)} товаров.
            {problems.length > 0 && ` Пропущены строки с ошибками: ${problems.slice(0, 10).join(', ')}${problems.length > 10 ? '…' : ''}.`}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={!rows?.length || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          Загрузить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Tile = ({ label, value, hint, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.25, color: color || tokens.ink }}>{value}</Typography>
      {hint && <Typography variant="body2" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

const CostsTab = ({ accounts, readOnly = false, mp }) => {
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const rowsPerPage = 50;

  useEffect(() => {
    if (!accounts.some((a) => a.id === accountId)) setAccountId(accounts[0]?.id || '');
  }, [accounts, accountId]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setError('');
    try {
      const { data } = await costsApi.list(accountId);
      setItems(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [accountId]);

  useEffect(() => { setItems(null); setSelected([]); load(); }, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((p) => {
      if (filter === 'unset' && p.cost_price != null) return false;
      if (filter === 'set' && p.cost_price == null) return false;
      return !q || String(p.nm_id).includes(q) || (p.vendor_code || '').toLowerCase().includes(q) || (p.title || '').toLowerCase().includes(q);
    });
  }, [items, query, filter]);

  const stats = useMemo(() => {
    const total = items?.length || 0;
    const set = items?.filter((p) => p.cost_price != null).length || 0;
    return { total, set, unset: total - set, soldUnset: items?.filter((p) => p.cost_price == null && p.sold_last_90 > 0).length || 0 };
  }, [items]);

  const downloadTemplate = () => {
    const data = [[mp === 'ozon' ? 'SKU Ozon' : 'Артикул WB', 'Артикул продавца', 'Название', 'Себестоимость'],
      ...(items || []).map((p) => [p.nm_id, p.vendor_code, p.title, p.cost_price == null ? '' : Number(p.cost_price)])];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Себестоимость');
    XLSX.writeFile(wb, mp === 'ozon' ? 'marqit_себестоимость_ozon.xlsx' : 'marqit_себестоимость.xlsx');
  };

  const pageRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const allOnPage = pageRows.length > 0 && pageRows.every((p) => selected.includes(p.nm_id));
  const selectedProducts = (items || []).filter((p) => selected.includes(p.nm_id));
  const done = (changed) => { if (changed) load(); };
  const share = stats.total ? Math.round((stats.set / stats.total) * 100) : 0;

  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', background: tokens.heroGradient }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2" color="text.secondary">Себестоимость заполнена</Typography>
                <Typography sx={{ fontWeight: 700, color: tokens.primaryDark }}>{share}%</Typography>
              </Stack>
              <Typography sx={{ fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.25 }}>
                {items ? `${formatNumber(stats.set)} из ${formatNumber(stats.total)}` : '—'}
              </Typography>
              <LinearProgress variant="determinate" value={share} sx={{ mt: 1.25, bgcolor: 'rgba(255,255,255,0.7)' }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Tile label="Без себестоимости" value={items ? formatNumber(stats.unset) : '—'} color={stats.unset ? tokens.warning : tokens.ink} />
        </Grid>
        <Grid item xs={6} md={3}>
          <Tile label="Продавались без неё" value={items ? formatNumber(stats.soldUnset) : '—'} hint="за 90 дней — прибыль по ним неточная"
            color={stats.soldUnset ? tokens.error : tokens.ink} />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }} sx={{ mb: 2 }}>
            {accounts.length > 1 && (
              <Select size="small" value={accountId} onChange={(e) => setAccountId(e.target.value)} inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 200 }}>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </Select>
            )}
            <TextField size="small" placeholder="Поиск: артикул, название" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
              inputProps={{ 'aria-label': 'Поиск товара' }} sx={{ minWidth: 260 }} />
            <SubTabs value={filter} onChange={(v) => { setFilter(v); setPage(0); }}
              items={[{ key: 'all', label: 'Все' }, { key: 'unset', label: 'Не заданы' }, { key: 'set', label: 'Заданы' }]} />
            <Box sx={{ flex: 1 }} />
            {readOnly && <Typography variant="body2" color="text.secondary">Только просмотр: изменять себестоимость может владелец или сотрудник с правом изменения</Typography>}
            {!readOnly && selected.length > 0 && (
              <Button variant="contained" onClick={() => setEditing(selectedProducts)}>Задать для выбранных ({selected.length})</Button>
            )}
            {!readOnly && <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setAdding(true)}>Добавить</Button>}
            {!readOnly && <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadTemplate} disabled={!items?.length}>Шаблон</Button>}
            {!readOnly && <Button variant="outlined" startIcon={<UploadFileOutlinedIcon />} onClick={() => setImporting(true)}>Загрузить</Button>}
          </Stack>

          {!items && <Skeleton variant="rounded" height={320} />}
          {items && rows.length === 0 && (
            <EmptyState title={items.length ? 'Ничего не найдено' : 'Товаров пока нет'}
              description={items.length ? 'Измените поиск или фильтр.' : 'Товары появятся после загрузки данных кабинета. Можно добавить артикул вручную.'} />
          )}
          {items && rows.length > 0 && (
            <Box sx={{ overflowX: 'auto', border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
              <Table size="small" aria-label="Себестоимость товаров">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={allOnPage} indeterminate={!allOnPage && pageRows.some((p) => selected.includes(p.nm_id))}
                        onChange={() => setSelected(allOnPage ? selected.filter((id) => !pageRows.some((p) => p.nm_id === id)) : [...new Set([...selected, ...pageRows.map((p) => p.nm_id)])])}
                        inputProps={{ 'aria-label': 'Выбрать все на странице' }} />
                    </TableCell>
                    <TableCell>Товар</TableCell>
                    <TableCell>Артикул продавца</TableCell>
                    <TableCell align="right">Себестоимость</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((p) => (
                    <TableRow key={p.nm_id} hover selected={selected.includes(p.nm_id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.includes(p.nm_id)} onChange={() => setSelected(selected.includes(p.nm_id) ? selected.filter((x) => x !== p.nm_id) : [...selected, p.nm_id])}
                          inputProps={{ 'aria-label': `Выбрать ${p.nm_id}` }} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar variant="rounded" src={p.photo_url || undefined} alt="" sx={{ width: 36, height: 46, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
                            <ImageOutlinedIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 380 }} noWrap title={p.title}>{p.title || 'Без названия'}</Typography>
                            <Typography variant="caption" color="text.secondary">{idPrefix(mp)} {p.nm_id}{p.brand ? ` · ${p.brand}` : ''}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{p.vendor_code || '—'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <ButtonBase onClick={() => setEditing([p])} disabled={readOnly} aria-label={`Изменить себестоимость ${p.nm_id}`}
                          sx={{ px: 1.25, py: 0.5, borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', border: `1px dashed ${p.cost_price == null ? tokens.warning : 'transparent'}`,
                            color: p.cost_price == null ? tokens.warning : tokens.ink, '&:hover': { bgcolor: tokens.primaryTint, borderColor: tokens.primary } }}>
                          {p.cost_price == null ? 'задать' : formatMoney(p.cost_price, { kopecks: true })}
                        </ButtonBase>
                        {p.valid_from && p.valid_from !== FROM_START && (
                          <Typography variant="caption" color="text.secondary" display="block">с {formatDate(p.valid_from)}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!readOnly && (
                          <Tooltip title="Изменить">
                            <IconButton size="small" onClick={() => setEditing([p])} aria-label={`Изменить ${p.nm_id}`}><EditOutlinedIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > rowsPerPage && (
                <TablePagination component="div" count={rows.length} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[rowsPerPage]}
                  onPageChange={(_, v) => setPage(v)} labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`} />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <SetCostDialog open={Boolean(editing)} products={editing || []} accountId={accountId} mp={mp}
        onClose={(changed) => { setEditing(null); if (changed) { setSelected([]); done(true); } }} />
      <AddArticleDialog open={adding} accountId={accountId} mp={mp} onClose={(changed) => { setAdding(false); done(changed); }} />
      <ImportDialog open={importing} accountId={accountId} mp={mp} onClose={(changed) => { setImporting(false); done(changed); }} />
    </>
  );
};

// Section "Себестоимость и налоги" of a marketplace.
const CostPricePage = () => {
  const { marketplace, accounts } = useOutletContext();
  const { profile } = useShell();
  const readOnly = !can(profile, 'costs', 'edit');
  const location = useLocation();
  const navigate = useNavigate();
  const tab = new URLSearchParams(location.search).get('tab') === 'taxes' ? 'taxes' : 'costs';
  const setTab = (v) => navigate({ search: v === 'taxes' ? '?tab=taxes' : '' }, { replace: true });

  return (
    <Box>
      <SubTabs items={[{ key: 'costs', label: 'Себестоимость товаров' }, { key: 'taxes', label: 'Налоги и НДС' }]} value={tab} onChange={setTab} sx={{ mb: 3 }} />
      {tab === 'taxes' ? <TaxesPanel marketplace={marketplace} readOnly={readOnly} />
        : hasSync(marketplace) ? <CostsTab accounts={accounts} readOnly={readOnly} mp={marketplace} />
          : <ComingSoon marketplace={marketplace} what="Товары и себестоимость" />}
    </Box>
  );
};

export default CostPricePage;
